// Beta-0.5D-B · 材質辞書 — Material Dictionary UI.
// Wraps the existing /api/doc-ai/materials* endpoints. No new store.

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ArrowsClockwise, CircleNotch, MagnifyingGlass, PencilSimple, X,
  Plus, Check, Warning, ClockCounterClockwise,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function EditDialog({ initial, onClose, onSaved }) {
  const isNew = !initial?.canonical_code;
  const [code, setCode] = useState(initial?.canonical_code || '');
  const [ja, setJa] = useState(initial?.display_name_ja || '');
  const [en, setEn] = useState(initial?.display_name_en || '');
  const [family, setFamily] = useState(initial?.material_family || '');
  const [aliases, setAliases] = useState((initial?.aliases || []).join(', '));
  const [ocr, setOcr] = useState((initial?.common_ocr_variants || []).join(', '));
  const [enabled, setEnabled] = useState(initial?.enabled !== false);
  const [busy, setBusy] = useState(false);

  const parse = (s) => s.split(',').map((x) => x.trim()).filter(Boolean);

  const save = async () => {
    if (!code.trim()) { toast.error('canonical_code は必須です'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/doc-ai/materials`, {
        canonical_code: code.trim(),
        display_name_ja: ja, display_name_en: en, material_family: family,
        aliases: parse(aliases), common_ocr_variants: parse(ocr),
        enabled,
      }, { headers: authHeader() });
      toast.success(isNew ? '追加しました' : '保存しました');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="material-edit-dialog" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <p className="text-[13px] font-semibold text-zinc-900">
            {isNew ? '材質を追加' : `編集: ${code}`}
          </p>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-700"
            data-testid="material-edit-cancel-x"><X size={14} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold text-zinc-700">正規コード</label>
            <input type="text" value={code} onChange={(e) => setCode(e.target.value)}
              disabled={!isNew}
              className="mt-1 w-full h-9 px-2 rounded-md border border-zinc-200 text-[13px] font-mono disabled:bg-zinc-50 disabled:text-zinc-500"
              data-testid="material-edit-code" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-zinc-700">日本語名</label>
              <input type="text" value={ja} onChange={(e) => setJa(e.target.value)}
                className="mt-1 w-full h-9 px-2 rounded-md border border-zinc-200 text-[13px]"
                data-testid="material-edit-ja" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-zinc-700">英語名</label>
              <input type="text" value={en} onChange={(e) => setEn(e.target.value)}
                className="mt-1 w-full h-9 px-2 rounded-md border border-zinc-200 text-[13px]"
                data-testid="material-edit-en" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-zinc-700">材質分類</label>
            <input type="text" value={family} onChange={(e) => setFamily(e.target.value)}
              placeholder="例: 鋼材 / アルミ / 樹脂"
              className="mt-1 w-full h-9 px-2 rounded-md border border-zinc-200 text-[13px]"
              data-testid="material-edit-family" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-zinc-700">エイリアス（カンマ区切り）</label>
            <input type="text" value={aliases} onChange={(e) => setAliases(e.target.value)}
              className="mt-1 w-full h-9 px-2 rounded-md border border-zinc-200 text-[12px] font-mono"
              data-testid="material-edit-aliases" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-zinc-700">OCR候補（カンマ区切り）</label>
            <input type="text" value={ocr} onChange={(e) => setOcr(e.target.value)}
              placeholder="OCRで読み取られた揺れ表記"
              className="mt-1 w-full h-9 px-2 rounded-md border border-zinc-200 text-[12px] font-mono"
              data-testid="material-edit-ocr" />
          </div>
          <label className="inline-flex items-center gap-2 text-[12px] text-zinc-700 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
              className="accent-teal-700" data-testid="material-edit-enabled" />
            有効
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}
            data-testid="material-edit-cancel">キャンセル</Button>
          <Button onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800"
            data-testid="material-edit-save">
            {busy ? <CircleNotch size={11} className="animate-spin mr-1" /> : <Check size={11} className="mr-1" />}
            保存
          </Button>
        </div>
      </div>
    </div>
  );
}

function MaterialRow({ m, health, onEdit, onLoadHealth, onToggle }) {
  return (
    <tr className="hover:bg-zinc-50/50" data-testid={`material-row-${m.canonical_code}`}>
      <td className="px-3 py-2 font-mono text-[11.5px] text-zinc-900">{m.canonical_code}</td>
      <td className="px-3 py-2 text-zinc-900">{m.display_name_ja || '—'}</td>
      <td className="px-3 py-2 text-zinc-700 text-[11.5px]">{m.display_name_en || '—'}</td>
      <td className="px-3 py-2 text-zinc-600 text-[11.5px]">{m.material_family || '—'}</td>
      <td className="px-3 py-2 text-zinc-600 text-[11px]">
        {(m.aliases || []).slice(0, 3).map((a, i) => (
          <span key={i} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded bg-zinc-100 font-mono text-[10.5px]">{a}</span>
        ))}
        {(m.aliases || []).length > 3 && (
          <span className="text-zinc-400 text-[10.5px]">+{(m.aliases || []).length - 3}</span>
        )}
      </td>
      <td className="px-3 py-2 text-zinc-600 text-[11px]">
        {(m.common_ocr_variants || []).slice(0, 3).map((a, i) => (
          <span key={i} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 font-mono text-[10.5px] text-amber-900">{a}</span>
        ))}
        {(m.common_ocr_variants || []).length > 3 && (
          <span className="text-zinc-400 text-[10.5px]">+{(m.common_ocr_variants || []).length - 3}</span>
        )}
      </td>
      <td className="px-3 py-2 text-center">
        <button type="button" onClick={() => onToggle(m)}
          data-testid={`material-toggle-${m.canonical_code}`}
          className={`px-1.5 py-0.5 rounded text-[10.5px] font-semibold ${
            m.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-zinc-100 text-zinc-500'}`}>
          {m.enabled ? '利用中' : '無効'}
        </button>
      </td>
      <td className="px-3 py-2 text-[11px] tabular-nums text-zinc-700">
        {health ? health.linked_documents.toLocaleString('ja-JP') : (
          <button type="button" onClick={() => onLoadHealth(m.canonical_code)}
            className="text-teal-700 hover:underline"
            data-testid={`material-health-load-${m.canonical_code}`}>関連書類</button>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <button type="button" onClick={() => onEdit(m)}
          className="text-teal-700 hover:underline text-[11px]"
          data-testid={`material-edit-${m.canonical_code}`}>
          <PencilSimple size={11} className="inline mr-0.5" />編集
        </button>
      </td>
    </tr>
  );
}

export default function MaterialsPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [family, setFamily] = useState('all');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [healthMap, setHealthMap] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/doc-ai/materials`, { headers: authHeader() });
      setRows(r.data.rows || []);
    } catch (e) { toast.error('材質の取得に失敗しました'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const families = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.material_family && s.add(r.material_family));
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => rows.filter((m) => {
    if (status === 'enabled' && !m.enabled) return false;
    if (status === 'disabled' && m.enabled) return false;
    if (family !== 'all' && (m.material_family || '') !== family) return false;
    if (q.trim()) {
      const s = q.toLowerCase();
      const hay = [m.canonical_code, m.display_name_ja, m.display_name_en,
        ...(m.aliases || []), ...(m.common_ocr_variants || [])]
        .map((x) => (x || '').toLowerCase()).join(' ');
      if (!hay.includes(s)) return false;
    }
    return true;
  }), [rows, q, family, status]);

  const loadHealth = async (code) => {
    try {
      const r = await axios.get(`${API}/api/doc-ai/materials/${encodeURIComponent(code)}/health`,
        { headers: authHeader() });
      setHealthMap((h) => ({ ...h, [code]: r.data }));
    } catch (e) { toast.error('健全性の取得に失敗しました'); }
  };
  const toggle = async (m) => {
    try {
      await axios.post(`${API}/api/doc-ai/materials/${encodeURIComponent(m.canonical_code)}/enabled`,
        { enabled: !m.enabled }, { headers: authHeader() });
      toast.success(m.enabled ? '無効化しました' : '有効化しました');
      await load();
    } catch (e) { toast.error('切り替えに失敗しました'); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 space-y-5" data-testid="materials-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">材質辞書</h1>
          <p className="text-[12px] text-zinc-600 mt-1">
            AI が書類から材質を読み取るときに使う正規辞書。既存の <span className="font-mono text-[11px]">/api/doc-ai/materials*</span> をそのまま使っています。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/settings/materials/unknown" className="text-[12px] text-teal-700 hover:underline"
            data-testid="materials-goto-unknown">
            <ClockCounterClockwise size={12} className="inline mr-0.5" />
            未登録材質のキュー
          </a>
          <Button variant="outline" onClick={load} disabled={loading} className="h-9 text-[12px]"
            data-testid="materials-refresh">
            {loading ? <CircleNotch size={13} className="animate-spin mr-1" />
                      : <ArrowsClockwise size={13} className="mr-1" />} 更新
          </Button>
          <Button onClick={() => setEditing({})} className="h-9 text-[12px] bg-teal-700 hover:bg-teal-800"
            data-testid="materials-add">
            <Plus size={13} className="mr-1" /> 材質を追加
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-zinc-200 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="材質を検索（コード・日本語・英語・エイリアス・OCR）"
              data-testid="materials-search"
              className="w-full h-8 pl-8 pr-8 rounded-md border border-zinc-200 text-[12px] focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400" />
            {q && (
              <button type="button" onClick={() => setQ('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400"><X size={11} weight="bold" /></button>
            )}
          </div>
          <select value={family} onChange={(e) => setFamily(e.target.value)}
            data-testid="materials-family-filter"
            className="h-8 px-2 rounded-md border border-zinc-200 text-[11.5px]">
            <option value="all">分類: すべて</option>
            {families.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="materials-status-filter"
            className="h-8 px-2 rounded-md border border-zinc-200 text-[11.5px]">
            <option value="all">状態: すべて</option>
            <option value="enabled">利用中のみ</option>
            <option value="disabled">無効のみ</option>
          </select>
          <span className="text-[10.5px] text-zinc-400 tabular-nums">{filtered.length} / {rows.length}</span>
        </div>
        <div className="max-h-[560px] overflow-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-zinc-50 text-zinc-500 sticky top-0">
              <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-left text-[10.5px] uppercase tracking-wider">
                <th>コード</th><th>日本語名</th><th>英語名</th><th>分類</th>
                <th>エイリアス</th><th>OCR候補</th>
                <th className="text-center">状態</th>
                <th>関連書類</th>
                <th className="text-right">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.map((m) => (
                <MaterialRow key={m.canonical_code} m={m}
                  health={healthMap[m.canonical_code]}
                  onEdit={setEditing}
                  onLoadHealth={loadHealth}
                  onToggle={toggle} />
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-[11.5px] text-zinc-400"
                  data-testid="materials-empty">
                  {loading ? '読み込み中…' : '材質が見つかりません'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditDialog initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}
